/**
 * Sanitize free-form custom parameters coming from the public track() API
 * and the GTM template. Known e-commerce keys keep their snake_case mapping;
 * unknown keys pass through under strict caps so a misconfigured tag cannot
 * bloat payloads or smuggle PII past the hashed setUserData path.
 */

import { debugLog } from "./state.js";
import {
  CUSTOM_DATA_MAP,
  CUSTOM_PARAM_KEY_PATTERN,
  MAX_CUSTOM_PARAMS,
  MAX_CUSTOM_STRING_LENGTH,
  MAX_CUSTOM_ARRAY_ITEMS,
  PII_KEY_BLOCKLIST,
} from "./constants.js";

/** Keys handled by track() itself; never forwarded as custom data. "rule" names a code rule, "eventId" is the dedupe id. */
const RESERVED_KEYS = ["value", "currency", "rule", "eventId"];

function sanitizeValue(value: unknown): unknown {
  if (typeof value === "string") {
    return value.slice(0, MAX_CUSTOM_STRING_LENGTH);
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  if (Array.isArray(value)) {
    const items: unknown[] = [];
    for (let i = 0; i < value.length && items.length < MAX_CUSTOM_ARRAY_ITEMS; i++) {
      const item = value[i];
      if (typeof item === "string") items.push(item.slice(0, MAX_CUSTOM_STRING_LENGTH));
      else if (typeof item === "number") items.push(item);
      else {
        // A basket line, which is a flat object and not smuggled nesting.
        //
        // Arrays used to keep strings and numbers only, so `contents`, the one
        // field every platform documents as a list of objects, arrived as an
        // EMPTY array. Not dropped, not refused: empty. Measured on the
        // WooCommerce test shop on 4 September 2026, where the plugin sent the
        // basket and the payload said `"contents": []`.
        const line = sanitizeFlatObject(item);
        if (line) items.push(line);
      }
    }
    return items;
  }
  // Functions and deeper nesting are dropped
  return undefined;
}

/** Max fields kept per basket line; a line has an id, a quantity and a price. */
const MAX_LINE_FIELDS = 8;

/**
 * One object inside an array, flattened to what a platform can read.
 *
 * Strings, numbers and booleans only, one level deep, and the same PII key
 * blocklist as the top level: an object is not a way to smuggle an e-mail
 * address past the hashed setUserData path.
 */
function sanitizeFlatObject(value: unknown): Record<string, unknown> | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;

  const line: Record<string, unknown> = {};
  let kept = 0;
  for (const key of Object.keys(value as Record<string, unknown>)) {
    if (kept >= MAX_LINE_FIELDS) break;
    if (!CUSTOM_PARAM_KEY_PATTERN.test(key)) continue;
    if (PII_KEY_BLOCKLIST.indexOf(key.toLowerCase()) !== -1) continue;
    const field = (value as Record<string, unknown>)[key];
    if (typeof field === "string") line[key] = field.slice(0, MAX_CUSTOM_STRING_LENGTH);
    else if (typeof field === "number" || typeof field === "boolean") line[key] = field;
    else continue;
    kept++;
  }
  return kept > 0 ? line : null;
}

export function buildCustomData(params: Record<string, unknown>): Record<string, unknown> | undefined {
  const customData: Record<string, unknown> = {};
  let count = 0;

  for (const key of Object.keys(params)) {
    if (RESERVED_KEYS.indexOf(key) !== -1) continue;
    if (params[key] == null) continue;
    if (count >= MAX_CUSTOM_PARAMS) {
      debugLog("customParams", "dropped (max " + MAX_CUSTOM_PARAMS + " keys)", key);
      continue;
    }

    // Known e-commerce keys keep their camelCase -> snake_case mapping
    if (CUSTOM_DATA_MAP[key]) {
      customData[CUSTOM_DATA_MAP[key]] = params[key];
      count++;
      continue;
    }

    if (!CUSTOM_PARAM_KEY_PATTERN.test(key)) {
      debugLog("customParams", "dropped (invalid key)", key);
      continue;
    }
    if (PII_KEY_BLOCKLIST.indexOf(key.toLowerCase()) !== -1) {
      debugLog("customParams", "dropped PII-like key '" + key + "' — use TheSocialTap.setUserData() instead");
      continue;
    }

    const value = sanitizeValue(params[key]);
    if (value === undefined) {
      debugLog("customParams", "dropped (unsupported value type)", key);
      continue;
    }
    customData[key] = value;
    count++;
  }

  return count > 0 ? customData : undefined;
}
